import { useState, useEffect } from 'react';
import { 
  Box,
  Typography,
  Button,
  CircularProgress,
  Card,
  CardContent,
  TextField,
  Snackbar,
  Alert,
  Stack,
  IconButton
} from '@mui/material';
import { getGroupsForAdminDashboard, addUserToGroup, deleteGroup } from '../apis/apis';
import { styled } from '@mui/material/styles';
import React from 'react';
import DeleteIcon from '@mui/icons-material/Delete';

const GroupsContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)', // Two columns
  gap: theme.spacing(2), // Space between cards
  margin: theme.spacing(2),
  overflowY: 'auto', // Enable vertical scrolling
  marginTop: theme.spacing(14), // Increased margin to avoid overlap with fixed header
  padding: theme.spacing(0, 2), // Add left and right padding
  paddingTop: theme.spacing(2), // Increased margin to avoid overlap with fixed header
  paddingBottom: theme.spacing(2), // Add padding to the bottom to prevent cutoff of shadows
}));

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%', // Ensure all cards are the same height
  boxShadow: theme.shadows[3],
  borderRadius: theme.shape.borderRadius,
}));

const FixedHeader = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  backgroundColor: theme.palette.background.default,
  zIndex: 1000,
  padding: theme.spacing(2),
  boxShadow: theme.shadows[2],
}));

export default function AdminDashboard() {
  const [groups, setGroups] = useState([]); // Placeholder for groups data
  const [loading, setLoading] = useState(true); // Loading state
  const [groupId, setGroupId] = useState(''); // State for group ID input
  const [userEmail, setUserEmail] = useState(''); // State for user email input
  const [snackbarOpen, setSnackbarOpen] = useState(false); // Snackbar state
  const [snackbarMessage, setSnackbarMessage] = useState(''); // Snackbar message
  const [snackbarSeverity, setSnackbarSeverity] = useState('success'); // Snackbar severity

  const fetchGroups = async () => {
    setLoading(true);
    const data = await getGroupsForAdminDashboard();
    setGroups(data);
    setLoading(false);
  };

  const handleAddUser = async () => {
    try {
      await addUserToGroup(groupId, userEmail);
      setSnackbarMessage('User added successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setGroupId('');
      setUserEmail('');
    } catch (error) {
      setSnackbarMessage(`Could not add user to group: ${error.message}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    try {
      await deleteGroup(groupId);
      setSnackbarMessage('Group Deleted Successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      setSnackbarMessage(`Could not delete group: ${error.message}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  }

  useEffect(() => {
    fetchGroups(); // Fetch groups on mount
  }, []); // Empty dependency array to run once on mount

  return (
    <Box component="main" sx={{ padding: 3 }}>
      <FixedHeader>
        <Typography component="h1" variant="h4" color="primary.main" align="center">
          Admin Dashboard
        </Typography>
        <Box sx={{ marginTop: 2, display: 'flex', alignItems: 'center' }}>
          <TextField
            label="Group ID"
            value={groupId}
            onChange={(e) => setGroupId(e.target.value)}
            size="small"
            sx={{ marginRight: 1, flex: 1 }}
          />
          <TextField
            label="User Email"
            value={userEmail}
            onChange={(e) => setUserEmail(e.target.value)}
            size="small"
            sx={{ marginRight: 1, flex: 1 }}
          />
          <Button variant="contained" onClick={handleAddUser}>
            Add User
          </Button>
        </Box>
      </FixedHeader>

      <GroupsContainer>
        {loading ? (
          <CircularProgress />
        ) : (
          groups.map((group) => (
            <StyledCard key={group.id}>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <Typography variant="h6" color="text.primary">{group.groupName}: {group.id}</Typography>
                  <Box flexGrow={1} /> {/* Pushes the icon to the right */}
                    <IconButton onClick={() => handleDeleteGroup(group.id)} aria-label="delete">
                      <DeleteIcon />
                    </IconButton>
                </Box>
                <Typography variant="subtitle1" color="text.secondary">Users: {group.users.length}</Typography>
                <Box display="flex" flexDirection="column" marginTop={1}>
                  {group.users.map((user) => (
                    <Typography key={user.id} variant="body2" color="text.primary">
                      {user.name} - {user.email}
                    </Typography>
                  ))}
                </Box>
              </CardContent>
            </StyledCard>
          ))
        )}
      </GroupsContainer>

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)}>
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
} 